import { isSSR } from '@k2/core/util'
import type { Platform } from '@k2/domain/campaigns'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useApiLoad, useApiRequest } from '@/api'
import { type Brand, type BrandDetail, useBrandsResource } from '../resources'
import { useBrandKitStore } from './brand-kit.store'

type CreateBrandBody = {
  name: string
  vertical: Brand['vertical']
  language: string
  avatarInitials: string
  color: string
  voice?: string | null
}
type UpdateBrandBody = Partial<CreateBrandBody> & { profileDocument?: string; research?: string | null }

export const useBrandStore = defineStore('brand', () => {
  const { getBrands, getBrand, setBrand, delBrand, setBrandVoiceSection, getBrandPlatforms, setBrandPlatforms } = useBrandsResource()
  const kitStore = useBrandKitStore()
  const router = useRouter()
  const { run, runScoped, loading, failure } = useApiRequest()
  const { run: runList, loading: listLoading, failure: listFailure } = useApiLoad()

  const brands = ref<Brand[]>([])

  const brandById = computed(() => new Map(brands.value.map(brand => [brand.id, brand])))

  let listedOrganizationId: string | undefined
  let loadedOrganizationId: string | undefined

  const loadBrands = async (organizationId: string) => {
    if (isSSR) return
    if (listedOrganizationId !== organizationId) {
      listedOrganizationId = organizationId
      loadedOrganizationId = undefined
      brands.value = []
    }

    const rows = await runList<Brand[]>(() => getBrands(organizationId))
    if (rows) {
      brands.value = rows
      loadedOrganizationId = organizationId
    }
  }

  let pending: { organizationId: string; request: Promise<void> } | undefined

  const getOrLoadBrands = (organizationId: string) => {
    if (loadedOrganizationId === organizationId) {
      listFailure.value = undefined
      return Promise.resolve()
    }
    if (pending?.organizationId !== organizationId)
      pending = { organizationId, request: loadBrands(organizationId).finally(() => (pending = undefined)) }
    return pending.request
  }

  // D5: the platforms each brand posts on, read when its page asks for them.
  const platforms = ref<Record<string, Platform[]>>({})

  const loadPlatforms = async (organizationId: string, brandId: string) => {
    if (isSSR) return
    const rows = await run<Platform[]>(() => getBrandPlatforms(organizationId, brandId))
    if (rows) platforms.value = { ...platforms.value, [brandId]: rows }
  }

  const savePlatforms = async (organizationId: string, brandId: string, chosen: Platform[]) => {
    const rows = await run<Platform[]>(() => setBrandPlatforms(organizationId, brandId, chosen))
    if (rows) platforms.value = { ...platforms.value, [brandId]: rows }
    return Boolean(rows)
  }

  const loadBrand = async (organizationId: string, brandId: string) => {
    if (isSSR) return
    return runScoped<BrandDetail>(() => getBrand(organizationId, brandId)).result
  }

  const create = async (organizationId: string, body: CreateBrandBody) => {
    const brand = await run<Brand>(() => setBrand(organizationId, body))
    if (brand) brands.value = [brand, ...brands.value]
    return brand
  }

  const saveBrand = async (organizationId: string, brandId: string, body: UpdateBrandBody) => {
    const brand = await run<BrandDetail>(() => setBrand(organizationId, brandId, body))
    if (brand) {
      const { profileDocument, research, ...listed } = brand
      brands.value = brands.value.map(row => (row.id === brandId ? { ...row, ...listed } : row))
    }
    return brand
  }

  const saveVoiceSection = async (organizationId: string, brandId: string, heading: string, body: string) => {
    const brand = await run<BrandDetail>(() => setBrandVoiceSection(organizationId, brandId, { heading, body }))
    if (!brand) return null
    brands.value = brands.value.map(row => (row.id === brandId ? { ...row, voice: brand.voice } : row))
    const kit = kitStore.kits[brandId]
    if (kit?.brand) kitStore.kits = { ...kitStore.kits, [brandId]: { ...kit, brand } }
    return brand
  }

  const removeBrand = async (organizationId: string, brandId: string) => {
    await run(() => delBrand(organizationId, brandId))
    if (failure.value) return false
    brands.value = brands.value.filter(row => row.id !== brandId)
    await router.push({ name: 'organization', params: { organizationId } })
    return true
  }

  return {
    brands,
    brandById,
    loading,
    failure,
    listLoading,
    listFailure,
    loadBrands,
    getOrLoadBrands,
    loadBrand,
    create,
    saveBrand,
    saveVoiceSection,
    platforms,
    loadPlatforms,
    savePlatforms,
    removeBrand
  }
})
